import React, { useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import _ from 'underscore';

import services from 'api/services';
import { useFreecodeContextData, useFreecodeContextRun } from 'hooks/useContext';
import { ObjectException } from 'global/errors';
import FormModal from 'components/Modals/formModal';
import { ModalTextArea } from 'components/Modals/styles';
import {
  ApiModalLabel,
  ApiModalInput,
  ApiTypeSelect,
  ApiParametersRow,
  ApiParameterDeleteContainer,
  DeleteIcon,
  ApiParameterCol,
  ApiParameterChildContainer,
  ApiParameterLabel,
  ErrorMessage,
} from './styles';

const { FreecodeCreatorService } = services;

//#region helpers

const defaultEmptyApi = {
  description: '',
  parameters: [],
  name: '',
  returns: undefined,
};

const mapKeyTypes = { int: 'int', str: 'string' };
const typeOptions = { int: 'int', bool: 'bool', float: 'double', str: 'string' };
const collectionTypeOptions = { list: 'list', map: 'map' };
const typeOptionsWithCollection = { '': 'None', ...typeOptions, ...collectionTypeOptions };

const createNewType = (newTypeIndex, isMapKey = false) => {
  if (isMapKey) return _.keys(mapKeyTypes)[newTypeIndex - 1];
  if (newTypeIndex === 5) return { kind: 'list', element: 'int' };
  if (newTypeIndex === 6) return { kind: 'map', key: 'int', value: 'int' };
  return _.keys(typeOptionsWithCollection)[newTypeIndex];
};

const validateName = (nameToValidate) => {
  const validNameRegexPattern = /^[a-z][a-z\d]*(\s[a-z\d]+)*$/;
  return validNameRegexPattern.test(nameToValidate);
};

//#end region helpers

export default function EditApiModal({ index, apiMethod, apiType, closeModal }) {
  const { testImpl, setTestImpl } = useFreecodeContextData();
  const { setCodeSol } = useFreecodeContextRun();

  const [apiUpdate, setApiUpdate] = useState(apiMethod || defaultEmptyApi);
  const [newParameterName, setNewParameterName] = useState('');
  const [loading, setLoading] = useState(false);

  const modalTitle = useMemo(() => `${index < 0 ? 'Add' : 'Edit'} ${apiType} API`, [index, apiType]);

  const handleNewParameterTypeChange = (newTypeIndex) => {
    let updatedApiMethod = { ...apiUpdate };
    updatedApiMethod.parameters.push({ name: newParameterName, type: createNewType(newTypeIndex) });
    setApiUpdate(updatedApiMethod);
    setNewParameterName('');
  };

  const handleDeleteParameter = (parameterIndex) => {
    let updatedApiMethod = { ...apiUpdate };
    updatedApiMethod.parameters.splice(parameterIndex, 1);
    setApiUpdate(updatedApiMethod);
  };

  const handleParameterNameChange = (parameterName, parameterIndex) => {
    let updatedApiMethod = { ...apiUpdate };
    updatedApiMethod.parameters[parameterIndex].name = parameterName;
    setApiUpdate(updatedApiMethod);
  };

  const handleParameterTypeChange = (newTypeIndex, parameterIndex) => {
    let updatedApiMethod = { ...apiUpdate };
    updatedApiMethod.parameters[parameterIndex].type = createNewType(newTypeIndex);
    setApiUpdate(updatedApiMethod);
  };

  const handleParameterTypeCollectionTypesChange = (newTypeIndex, typeKey, parent, parameterIndex) => {
    let updatedApiMethod = { ...apiUpdate };
    const newType = createNewType(newTypeIndex, typeKey === 'key');
    if (parent !== '') updatedApiMethod.parameters[parameterIndex].type[parent][typeKey] = newType;
    else updatedApiMethod.parameters[parameterIndex].type[typeKey] = newType;
    setApiUpdate(updatedApiMethod);
  };

  const handleReturnTypeChange = (newTypeIndex) => {
    let returns = createNewType(newTypeIndex);
    if (!returns) returns = undefined;
    setApiUpdate({ ...apiUpdate, returns });
  };

  const handleReturnTypeCollectionTypesChange = (newTypeIndex, typeKey, parent) => {
    let updatedApiMethod = { ...apiUpdate };
    const newType = createNewType(newTypeIndex, typeKey === 'key');
    if (parent !== '') updatedApiMethod.returns[parent][typeKey] = newType;
    else updatedApiMethod.returns[typeKey] = newType;
    setApiUpdate(updatedApiMethod);
  };

  const validateTypeData = (origin, type) => {
    if (!type) throw new ObjectException(`${origin} type undefined`, 'request error');
    if (typeof type === 'string') {
      if (Object.keys(typeOptions).includes(type)) return;
      throw new ObjectException(`${origin} invalid type ${type}`, 'request error');
    }

    if (!Object.keys(collectionTypeOptions).includes(type?.kind)) {
      throw new ObjectException(`${origin} unknown complex type ${type.kind}`, 'request error');
    }
    if (type.kind === 'list') validateTypeData(origin, type.element);
    if (type.kind === 'map') {
      validateTypeData(origin, type.key);
      validateTypeData(origin, type.value);
    }
  };

  const validateUpdate = () => {
    if (!apiUpdate?.name?.trim?.()?.length > 0) throw new ObjectException('Name is required', 'request error');
    if (!validateName(apiUpdate.name)) throw new ObjectException('Name is not valid', 'request error');

    let newValues = [...testImpl.api[apiType]];
    if (index >= 0) newValues[index] = apiUpdate;
    else newValues.push(apiUpdate);
    let apiNames = newValues.map((param) => param.name);
    if (newValues.length > new Set(apiNames).size) throw new ObjectException('Duplicate api name', 'request error');

    if (apiUpdate.returns && apiUpdate.returns !== 'None' && typeof apiUpdate.returns === 'string')
      validateTypeData('return', apiUpdate.returns);

    const paramNames = apiUpdate.parameters.map((param) => param.name);
    if (paramNames.length > new Set(paramNames).size)
      throw new ObjectException('Duplicate parameter name', 'request error');
    for (const parameter of apiUpdate.parameters) {
      if (!validateName(parameter.name))
        throw new ObjectException(`Invalid parameter name ${parameter.name}`, 'request error');
      validateTypeData('parameter', parameter.type);
    }
  };

  const saveOrEditApiMethod = () => {
    if (loading) return toast.error('Already Saving');
    setLoading(true);

    const doConfirm = async () => {
      try {
        validateUpdate();

        let newApi = { ...apiUpdate };
        if (newApi.returns === 'None') delete newApi.returns;

        let fullApi = { ...testImpl.api };
        if (index >= 0) fullApi[apiType][index] = newApi;
        else fullApi[apiType].push(newApi);

        let response;
        if (apiType === 'solution') {
          response = await FreecodeCreatorService.updateApiSolution(testImpl.test, testImpl._id, fullApi, newApi);
        } else if (apiType === 'challenge') {
          response = await FreecodeCreatorService.updateApiImplementation(testImpl.test, testImpl._id, fullApi, newApi);
        }
        if (response.error) throw new ObjectException(response.error?.message || 'delete failed', 'response error');
        if (!response?.api) throw new ObjectException('Response missing new api data', 'response error');

        setTestImpl((oldTestImpl) => ({
          ...oldTestImpl,
          solution: response.solution,
          challenge: response.challenge,
          api: response.api,
          generated: { ...response.generated, peekResponse: oldTestImpl.generated.peekResponse },
        }));
        setCodeSol(response.solution);
        toast.success('API updated');
        closeModal();
      } catch (e) {
        toast.error(typeof e === 'string' ? e : e?.message || 'Could not reset');
      } finally {
        setLoading(false);
      }
    };
    doConfirm();
  };

  const renderParameters = () => {
    return apiUpdate.parameters.map((param, index) => {
      return (
        <React.Fragment key={index}>
          <ApiParametersRow>
            <ApiParameterCol>
              <ApiModalInput
                placeholder="Parameter Name"
                value={param.name}
                onChange={(e) => handleParameterNameChange(e.target.value, index)}
              />
            </ApiParameterCol>
            <ApiParameterCol col="col-5">
              <TypeSelect
                selectedType={param.type}
                onChangeHandler={(newTypeIndex) => handleParameterTypeChange(newTypeIndex, index)}
                allowCollections
              />
            </ApiParameterCol>
            <ApiParameterDeleteContainer>
              <DeleteIcon onClick={() => handleDeleteParameter(index)} />
            </ApiParameterDeleteContainer>
          </ApiParametersRow>
          {param.type?.kind && (
            <ApiParametersRow marginBottom="1.5rem">
              {renderCollectionTypeSelector(param.type, (newTypeIndex, typeKey, parent) =>
                handleParameterTypeCollectionTypesChange(newTypeIndex, typeKey, parent, index)
              )}
            </ApiParametersRow>
          )}
        </React.Fragment>
      );
    });
  };

  const TypeSelect = ({
    selectedType,
    onChangeHandler,
    isMapKey = false,
    allowCollections = false,
    allowSelectNone = false,
    disabled = false,
  }) => {
    return (
      <ApiTypeSelect
        disabled={disabled}
        value={typeOptionsWithCollection[selectedType ? (selectedType.kind ? selectedType.kind : selectedType) : '']}
        onChange={(e) => onChangeHandler(allowSelectNone ? e.target.selectedIndex : e.target.selectedIndex + 1)}
      >
        {allowSelectNone && <option key="">None</option>}
        {_.map(isMapKey ? mapKeyTypes : typeOptions, (val, key) => {
          return <option key={key}>{val}</option>;
        })}
        {allowCollections &&
          _.map(collectionTypeOptions, (val, key) => {
            return <option key={key}>{val}</option>;
          })}
      </ApiTypeSelect>
    );
  };

  const renderTypeSelector = (type, onChangeHandler) => {
    return (
      <ApiTypeSelect
        value={typeOptionsWithCollection[type ? (type.kind ? type.kind : type) : '']}
        onChange={(e) => onChangeHandler(e.target.selectedIndex)}
      >
        {_.map(typeOptionsWithCollection, (val, key) => {
          return <option key={key}>{val}</option>;
        })}
      </ApiTypeSelect>
    );
  };

  const renderCollectionTypeSelector = (type, onChangeHandler, allowCollections = true, parent = '') => {
    return (
      <>
        {type.kind === 'list' ? (
          <>
            <ApiParameterCol col="col-12">
              <ApiParameterLabel>Type:</ApiParameterLabel>
              <TypeSelect
                selectedType={type.element}
                onChangeHandler={(selectedIndex) => onChangeHandler(selectedIndex, 'element', parent)}
                allowCollections={allowCollections}
              />
              <ApiParameterChildContainer>
                {allowCollections &&
                  type.element.kind &&
                  renderCollectionTypeSelector(type.element, onChangeHandler, false, 'element')}
              </ApiParameterChildContainer>
            </ApiParameterCol>
          </>
        ) : (
          <>
            <ApiParameterCol col="col-12">
              <ApiParameterLabel>Key:</ApiParameterLabel>
              <TypeSelect
                isMapKey
                selectedType={type.key}
                onChangeHandler={(selectedIndex) => onChangeHandler(selectedIndex, 'key', parent)}
              />
            </ApiParameterCol>
            <ApiParameterCol col="col-12">
              <ApiParameterLabel>Value:</ApiParameterLabel>
              <TypeSelect
                selectedType={type.value}
                onChangeHandler={(selectedIndex) => onChangeHandler(selectedIndex, 'value', parent)}
                allowCollections={allowCollections}
              />
              <ApiParameterChildContainer>
                {allowCollections &&
                  type.value.kind &&
                  renderCollectionTypeSelector(type.value, onChangeHandler, false, 'value')}
              </ApiParameterChildContainer>
            </ApiParameterCol>
          </>
        )}
      </>
    );
  };

  return (
    <FormModal
      title={modalTitle}
      confirmButtonText="Save"
      onCancel={() => !loading && closeModal()}
      onConfirm={saveOrEditApiMethod}
      loading={loading}
    >
      All names must be space-separated words containing lowercase letters and numbers, and may not start with a number.
      Specific language formatting will be done automatically.
      <ApiModalInput
        marginTop
        type="text"
        placeholder="Name"
        value={apiUpdate.name}
        onChange={(e) => setApiUpdate({ ...apiUpdate, name: e.target.value })}
      />
      {apiUpdate.name !== '' && !validateName(apiUpdate.name) && (
        <ErrorMessage>
          Name must be space-separated words containing lowercase letters and numbers, and may not start with a number!
        </ErrorMessage>
      )}
      <ModalTextArea
        value={apiUpdate.description}
        onChange={(e) => setApiUpdate({ ...apiUpdate, description: e.target.value })}
        placeholder="API description available to user"
      />
      <ApiModalLabel>Parameters</ApiModalLabel>
      <ApiParametersRow>
        <ApiParameterCol>
          <ApiModalInput
            value={newParameterName}
            placeholder="Parameter Name"
            onChange={(e) => setNewParameterName(e.target.value)}
          />
        </ApiParameterCol>
        <ApiParameterCol col="col-5">
          <TypeSelect
            type="None"
            disabled={!validateName(newParameterName)}
            onChangeHandler={(selectedIndex) => handleNewParameterTypeChange(selectedIndex)}
            allowSelectNone
            allowCollections
          />
        </ApiParameterCol>
        <ApiParameterDeleteContainer></ApiParameterDeleteContainer>
        {newParameterName !== '' && !validateName(newParameterName) && (
          <ApiParameterCol col="col-12">
            <ErrorMessage>
              Name must be space-separated words containing lowercase letters and numbers, and may not start with a
              number!
            </ErrorMessage>
          </ApiParameterCol>
        )}
      </ApiParametersRow>
      {renderParameters()}
      <ApiModalLabel>Return Type</ApiModalLabel>
      <ApiParametersRow>
        <ApiParameterCol col="col-3">{renderTypeSelector(apiUpdate.returns, handleReturnTypeChange)}</ApiParameterCol>
        {apiUpdate.returns?.kind &&
          renderCollectionTypeSelector(apiUpdate.returns, handleReturnTypeCollectionTypesChange)}
      </ApiParametersRow>
    </FormModal>
  );
}
